import React from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Modal, OverlayTrigger, Tooltip } from 'react-bootstrap-5';
import { faListUl } from '@fortawesome/pro-solid-svg-icons';
import snakecaseKeys from 'snakecase-keys';

import EmptyState from 'shared/empty_state';
import PageHeader from 'components/page_header';
import SearchPage from 'components/search_page';
import TaskForm from 'components/tasks/task_form';
import ContactPreview from 'components/contacts/contact_preview';
import LoadingSpinner from 'components/loading_spinner';

import ActionsDropdown from './actions_dropdown';
import CallList from './call_list';
import CallListSidebar from './call_list_sidebar';
import CallListCreationModal from './call_list_creation_modal';
import CallListRenamingModal from './call_list_renaming_modal';
import CallListDeletionModal from './call_list_deletion_modal';
import CallBulkDeleteModal from './call_bulk_delete_modal';
import CallBulkRemoveModal from './call_bulk_remove_modal';

import { retrieveTasks, getCallList } from './store/actions/search';
import { addCallList, handleRemoveCallList, updateCallList } from './store/actions/ui';
import { setCallListId } from './store/actions/query';
import { addTask, toggleTaskState } from './store/actions/tasks';

import { getCurrentCallList, sortTasksById } from './store/selectors/search';

import './call_list_index.scss';

export class CallLists extends React.Component {
  static propTypes = {
    activeCallListId: PropTypes.number,
    addCallList: PropTypes.func.isRequired,
    addTask: PropTypes.func.isRequired,
    availableDatabases: PropTypes.arrayOf(PropTypes.array),
    callLists: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string
      })
    ).isRequired,
    contactPreviewProps: PropTypes.object.isRequired,
    currentCallList: PropTypes.object.isRequired,
    currentUser: PropTypes.shape({
      id: PropTypes.number.isRequired
    }).isRequired,
    databaseMembers: PropTypes.array.isRequired,
    getCallList: PropTypes.func.isRequired,
    isFetching: PropTypes.bool,
    previewOverlayRecordId: PropTypes.number,
    previewOverlayRecordType: PropTypes.string,
    previewOverlayTaskId: PropTypes.number,
    removeCallList: PropTypes.func.isRequired,
    retrieveTasks: PropTypes.func.isRequired,
    setCallListId: PropTypes.func.isRequired,
    tasks: PropTypes.array.isRequired,
    toggleTaskState: PropTypes.func.isRequired,
    updateCallList: PropTypes.func.isRequired,
  };

  state = {
    activeCallListId: null,
    previewOverlayCallId: this.props.contactPreviewProps.callId,
    previewOverlayCallListId: this.props.contactPreviewProps.callListId,
    previewOverlayRecordId: this.props.contactPreviewProps.contactId,
    previewOverlayRecordType: this.props.contactPreviewProps.contactType,
    previewOverlayTaskId: this.props.contactPreviewProps.taskId,
    showCreateCallListModal: false,
    showDeleteCallListModal: false,
    showRenameCallListModal: false,
    showTaskForm: false,
    taskFormErrors: null
  }

  componentDidMount = () => {
    const { getCallList, retrieveTasks } = this.props;

    new Promise(function(resolve) {
      resolve(getCallList());
    }).then(() => {
      retrieveTasks();
    }).catch(() => {
      return;
    });
  }

  componentDidUpdate = (prevProps) => {
    const {
      previewOverlayRecordId: prevRecordId,
      previewOverlayRecordType: prevRecordType
    } = prevProps;

    const {
      previewOverlayRecordId,
      previewOverlayRecordType,
      previewOverlayTaskId
    } = this.props;

    if (prevRecordId !== previewOverlayRecordId || prevRecordType !== previewOverlayRecordType) {
      this.setState({ previewOverlayRecordId, previewOverlayRecordType, previewOverlayTaskId });
    }
  }

  findActiveCallList = () => this.props.callLists[this.props.activeCallListId];

  onCreateCallList = () => {
    this.setState({ showCreateCallListModal: true });
  }

  onCreateCallListModalClose = (newCallList) => {
    if (newCallList !== undefined) {
      this.props.addCallList(newCallList);
    }
    this.setState({ showCreateCallListModal: false });
  }

  onRenameCallList = () => {
    this.setState({ showRenameCallListModal: true });
  }

  onRenameCallListModalClose = (updatedCallList) => {
    if (updatedCallList !== undefined) {
      this.props.updateCallList(updatedCallList);
    }
    this.setState({ showRenameCallListModal: false });
  }

  onRemoveTasks = () => {
    this.setState({ showRemoveTasksModal: true });
  }

  onDeleteTasks = () => {
    this.setState({ showDeleteTasksModal: true });
  }

  onDeleteCallList = () => {
    this.setState({ showDeleteCallListModal: true });
  }

  onDeleteCallListModalClose = (deletionSuccess) => {
    const { activeCallListId, removeCallList } = this.props;

    if (deletionSuccess) {
      removeCallList(activeCallListId);
    }
    this.setState({ showDeleteCallListModal: false });
  }

  canStartCallList = () => {
    const { tasks } = this.props;
    return tasks.length > 0;
  }

  handlePreviewContact = () => {
    if (!this.canStartCallList()) return null;
  }

  handleStartCallList = () => {
    if (!this.canStartCallList()) return null;

    const {
      activeCallListId,
      contactPreviewProps,
      tasks
    } = this.props;
    const { displayActions: { slideOpen } } = contactPreviewProps;

    const activeTask = tasks[0];
    let record;

    if (activeTask && activeTask.associatedModels.length > 0) {
      record = activeTask.associatedModels.find((am) => {
        return (am.associatedModelType === 'Contacts::Person' || am.associatedModelType === 'Contacts::Company');
      });
    }

    if (record == null) {
      record = {};
    }

    this.setState({
      previewOverlayCallListId: activeCallListId,
      previewOverlayRecordId: record.associatedModelId || '',
      previewOverlayRecordType: record.associatedModelType || '',
      previewOverlayTaskId: activeTask.id
    });

    window[slideOpen]();
    sessionStorage.setItem('previewDisplay', true);
  }

  handleEndCallList = () => {
    const { contactPreviewProps } = this.props;
    const { displayActions: { slideClose } } = contactPreviewProps;

    this.setState({
      previewOverlayCallListId: null,
      previewOverlayTaskId: null
    });

    window[slideClose]();
    sessionStorage.setItem('previewDisplay', false);
  }

  handleEndCall = () => {
    this.setState({ previewOverlayCallId: false });
  }

  handleStartCall = () => {
    this.setState({ previewOverlayCallId: true });
  }

  handleCycleTask = (record, task) => {
    this.setState({
      previewOverlayRecordId: record.associatedModelId,
      previewOverlayRecordType: record.associatedModelType,
      previewOverlayTaskId: task.id
    });
  }

  handleListChange = (callListId) => {
    const { setCallListId, retrieveTasks } = this.props;

    setCallListId(callListId);
    retrieveTasks();
  };

  handleTaskFormOpen = () => {
    this.setState({
      showTaskForm: true
    });
  }

  handleTaskSave = (task) => {
    const { addTask } = this.props;

    addTask(
      task,
      this.handleTaskFormClose,
      errors => this.setState({ taskFormErrors: errors })
    );
  }

  handleTaskFormClose = () => {
    this.setState({
      showTaskForm: false,
      taskFormErrors: null
    });
  }

  render() {
    const { activeCallListId, callLists, isFetching } = this.props;
    return (
      <div className="call-list-index h-100">
        { this.renderPageHeader() }

        <SearchPage>
          <div className="d-flex px-4 pt-4 w-100 h-100">
            <CallListSidebar
              activeCallListId={activeCallListId}
              callLists={Object.values(callLists).reverse()}
              visible
              onListClick={this.handleListChange}
            />

            <div className="d-flex flex-column px-4 pt-4 w-100">
              {isFetching ? <LoadingSpinner /> : this.renderCallList()}
            </div>
          </div>
        </SearchPage>

        <CallBulkDeleteModal />
        <CallBulkRemoveModal />
        { this.renderDeleteCallListModal() }
        { this.renderCreateCallListModal() }
        { this.findActiveCallList() && this.renderRenameCallListModal() }
        { this.renderCreateTaskModal() }
        { this.renderPreviewOverlay() }
      </div>
    );
  }

  renderTaskForm() {
    const { activeCallListId, availableDatabases, callLists, currentUser, databaseMembers } = this.props;

    const activeCallList = activeCallListId == null ? null : callLists[activeCallListId];

    return (
      <TaskForm
        assigneeOptions={snakecaseKeys(databaseMembers)}
        availableDatabaseOptions={availableDatabases}
        defaultTaskAssignee={snakecaseKeys(databaseMembers.find(member => member.id === currentUser.id) || {})}
        defaultTaskList={activeCallList}
        errors={this.state.taskFormErrors}
        isFromCallList={true}
        onCancel={this.handleTaskFormClose}
        onSave={this.handleTaskSave}
      />
    );
  }

  renderCreateTaskModal() {
    const { showTaskForm } = this.state;
    return (
      <Modal
        backdrop="static"
        className="contact-modal overflow-y-auto"
        name="task-form-modal"
        show={showTaskForm}
        onHide={this.handleTaskFormClose}
      >
        <Modal.Header closeButton>
          <Modal.Title>Create A Task</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {this.renderTaskForm()}
        </Modal.Body>
      </Modal>
    );
  }



  renderNoCallListsMessage = () => {
    return (
      <EmptyState
        bodyText="Click “+ Create New Call List” or add Contact to a Call List from Database or Projects."
        header="No Current Call Lists"
        icon={faListUl}
      />
    );
  };

  renderCallList = () => {
    const { callLists } = this.props;

    if (Object.keys(callLists).length < 1) {
      return this.renderNoCallListsMessage();
    }

    return (
      <CallList
        filterButtons={[
          <ActionsDropdown
            key={null}
            onDeleteCallList={this.onDeleteCallList}
            onRenameCallList={this.onRenameCallList}
          />,
          this.renderStartCallListButton()
        ]}
        newCallButtonClick={this.handleTaskFormOpen}
        newCallButtonText="+ Add Call to this list"
      />
    );
  };

  renderDeleteCallListModal = () => (
    <CallListDeletionModal
      callList={this.findActiveCallList()}
      shown={this.state.showDeleteCallListModal}
      onClose={this.onDeleteCallListModalClose}
    />
  );

  renderCreateCallListModal = () => (
    <CallListCreationModal
      shown={this.state.showCreateCallListModal}
      onClose={this.onCreateCallListModalClose}
    />
  );

  renderRenameCallListModal = () => (
    <CallListRenamingModal
      callList={this.findActiveCallList()}
      shown={this.state.showRenameCallListModal}
      onClose={this.onRenameCallListModalClose}
    />
  );

  renderStartCallListButton = () => {
    const { activeCallListId } = this.props;
    const { previewOverlayCallId, previewOverlayCallListId } = this.state;
    const callListActive = previewOverlayCallListId && previewOverlayCallListId === activeCallListId;
    let tooltipPrompt;

    if (previewOverlayCallId) tooltipPrompt = 'You cannot start a call list while on a call';
    else if (callListActive) tooltipPrompt = 'This call list is already active';

    const toolTip = (
      <Tooltip className="filter" id="preview-tooltip" placement="top">
        <span>
          {tooltipPrompt}
        </span>
      </Tooltip>
    );

    if (previewOverlayCallId || callListActive) {
      return (
        <OverlayTrigger overlay={toolTip} placement="top">
          <div className="btn btn-primary me-3 disabled">
            Start Call List
          </div>
        </OverlayTrigger>
      );
    } else {
      return (
        <button
          className="btn btn-primary me-3"
          disabled={!this.canStartCallList()}
          key="start-call-button"
          onClick={this.handleStartCallList}
        >
          Start Call List
        </button>
      );
    }
  }

  renderPageHeader = () => (
    <PageHeader
      iconDescription={'Quickly organize and run through your Call Lists'}
      iconText={'Call Lists'}
    >
      <div className="btn btn-primary" onClick={this.onCreateCallList}>
        New Call List
      </div>
    </PageHeader>
  );

  renderPreviewOverlay = () => {
    const {
      contactPreviewProps,
      toggleTaskState
    } = this.props;

    const {
      previewOverlayCallListId,
      previewOverlayRecordId,
      previewOverlayRecordType,
      previewOverlayTaskId
    } = this.state;

    const { querySelector } = contactPreviewProps;

    return ReactDOM.createPortal(
      <ContactPreview
        {...contactPreviewProps}
        callListId={previewOverlayCallListId}
        contactId={previewOverlayRecordId}
        contactType={previewOverlayRecordType}
        taskId={previewOverlayTaskId}
        onCompleteTask={toggleTaskState}
        onCycleTask={this.handleCycleTask}
        onEndCall={this.handleEndCall}
        onEndCallList={this.handleEndCallList}
        onStartCall={this.handleStartCall}
      />,
      document.querySelector(querySelector)
    );
  }
}

const mapStateToProps = state => ({
  activeCallListId: state.query.callListId,
  availableDatabases: state.static.availableDatabases,
  callLists: state.ui.callLists,
  currentCallList: getCurrentCallList(state),
  contactPreviewProps: state.ui.contactPreviewProps,
  currentUser: state.static.currentUser,
  databaseMembers: state.static.databaseMembers,
  isFetching: state.search.isFetching,
  previewOverlayRecordId: state.ui.previewOverlayRecordId,
  previewOverlayRecordType: state.ui.previewOverlayRecordType,
  previewOverlayTaskId: state.ui.previewOverlayTaskId,
  tasks: sortTasksById(state)
});

const mapDispatchToProps = {
  addCallList,
  addTask,
  getCallList,
  removeCallList: handleRemoveCallList,
  retrieveTasks,
  setCallListId,
  toggleTaskState,
  updateCallList,
};

export default connect(mapStateToProps, mapDispatchToProps)(CallLists);
